import axios from '@fs/zion-axios'
import { baseUrl } from '@fs/zion-config'
import { type SearchResult } from '../types'

type SolrSearchResponse = {
  results: SearchResult[]
}

/**
 * Calls the real Solr search endpoint for Learn to Rank.
 * @param query - The search text (e.g., "yuma az").
 * @returns Object with a `results` array.
 */
export const solrSearch = async (query: string): Promise<SolrSearchResponse> => {
  const text = (query || '').trim()
  if (!text) {
    return { results: [] }
  }

  const placeServiceBase = `${baseUrl || ''}/service/standards/place/ws-ui`
  const url = `${placeServiceBase}/places/solr-search`

  const response = await axios.get(url, {
    params: {
      text,
      limit: 50,
      output: 'summary',
      partial: true,
    },
    placesAPI: true,
    fsUserAgentChainSuffix: 'zion-standards',
  })

  const data = response.data || {}

  // Flatten all hits from all searchResults
  const rawResults = (data.searchResults || []).flatMap((sr: { result?: unknown[] }) => sr?.result || []) || []

  // Normalize into the shape your UI expects
  const results: SearchResult[] = rawResults.map((r: Record<string, unknown>) => {
    const rep = (r.rep || {}) as Record<string, unknown>
    const display = (rep.display || {}) as Record<string, unknown>
    const fullDisplay = (rep.fullDisplay || {}) as Record<string, unknown>
    const displayName = (display.name || '') as string
    const fullDisplayName = fullDisplay.name as string

    return {
      id: rep.id as string,
      displayName,
      fullDisplayName,
      // ADD BOTH SCORES HERE
      rawScore: (r.rawScore as number) ?? null,
      relevanceScore: (r.relevanceScore as number) ?? null,
      // Keep your existing UI-friendly fields:
      names: [display.name].filter(Boolean) as string[],

      // keep full objects in case you need more later
      rep,
      tokenMatches: r.tokenMatches,
      originalIndex: 0, // Will be set by caller
    }
  })

  return { results }
}
